import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';

interface ImportResult {
  imported: number;
  skipped: number;
  errors: string[];
}

export default function LibraryImport() {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.upload<ImportResult>('/libraries/import-csv', formData);
      setResult(res);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <h1>Import Libraries from CSV</h1>

      <div className="card">
        <h2>CSV Format</h2>
        <p>Upload a CSV with a header row. Required columns: <strong>name</strong>, <strong>city</strong>, <strong>state</strong>.</p>
        <p>Optional columns:</p>
        <code style={{ display: 'block', padding: '0.5rem', background: '#f5f5f5', fontSize: '0.85rem' }}>
          system_name, branch_level, region, lat, lng, ils_vendor, z3950_host, z3950_port, z3950_database, sru_base_url, sru_record_schema, opac_url, ingest_method, status, confidence, notes
        </code>
        <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
          If <strong>ingest_method</strong> is omitted, it auto-detects from connection fields (z3950_host &rarr; z3950, sru_base_url &rarr; sru, opac_url &rarr; opac_scrape).
          Default status is "needs_review".
        </p>
      </div>

      <div style={{ marginTop: '1rem' }}>
        <input
          type="file"
          accept=".csv,.txt"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />
        <button onClick={handleUpload} disabled={!file || uploading} style={{ marginLeft: '0.5rem' }}>
          {uploading ? 'Importing...' : 'Upload & Import'}
        </button>
      </div>

      {error && <p className="error" style={{ marginTop: '1rem' }}>{error}</p>}

      {result && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <h2>Import Results</h2>
          <p><strong>{result.imported}</strong> libraries imported, <strong>{result.skipped}</strong> skipped.</p>
          {result.errors.length > 0 && (
            <>
              <h3>Errors:</h3>
              <ul>
                {result.errors.map((err, i) => <li key={i} style={{ color: 'red' }}>{err}</li>)}
              </ul>
            </>
          )}
          <button onClick={() => navigate('/libraries')} style={{ marginTop: '0.5rem' }}>
            View Libraries
          </button>
        </div>
      )}
    </div>
  );
}
